// Order seems to affect how the CSS behaves. Let's ignore sorting in this file for now
import "@/sentry";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Route } from "react-router-dom";

// ----------------------------------------------------

import "../css/global.css";

import "@/translations";
import { ROUTES, INTERNAL_ROUTES } from "@/routes_new";
import { PublicAlertView, PublicTagView } from "@/views/PublicAlertView";
import ScrollToTop from "@components/ScrollToTop";
import { AudioPlayerContextProvider } from "@/contexts/GlobalAudioPlayerContext";
import I18nProvider from "@/containers/I18nProvider";
import TrackPageView from "@/containers/TrackPageView";
import AppRoot from "@/views/a";
import LocalizedPublicRoot from "@/views/[lang]";
import MessageProvider from "@/containers/Messages/MessageProvider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import ActivationView from "@/views/ActivationView";
import ChannelDetailView from "@/views/internal/channel_details/ChannelDetailView";
import ChannelListView from "@/views/internal/channels/ChannelListView";
import InternalRoot from "@/views/internal/InternalRoot";
import { i18n } from "@lingui/core";
import { messages as en } from "@/locales/en/messages";
import { messages as sv } from "@/locales/sv/messages";
import SignupViewNew from "@/views/SignupNew";
import { Toaster } from "@components/ui/sonner";
import { TooltipProvider } from "@components/ui/tooltip";
import SignupRedirectView from "@/views/SignupRedirect";
import LoginView from "@/views/LoginView";
import ErrorBoundary from "@components/ErrorBoundary";

i18n.load("en", en);
i18n.load("sv", sv);
i18n.activate("en");
const queryClient = new QueryClient();

const RootApp = () => (
  <MessageProvider>
    <BrowserRouter>
      <ScrollToTop />
      <AudioPlayerContextProvider>
        <I18nProvider>
          <TooltipProvider delayDuration={700}>
            <Route exact path={ROUTES.LOGIN} component={LoginView} />
            <Route path={ROUTES.ACTIVATION} component={ActivationView} />
            <Route exact path={ROUTES.SIGNUP} component={SignupRedirectView} />
            <Route path={ROUTES.SIGNUP_LOCALIZED} component={SignupViewNew} />
            <Route path={ROUTES.ALERT_PUBLIC}>
              <PublicAlertView />
            </Route>
            <Route path={ROUTES.TAG_PUBLIC}>
              <PublicTagView />
            </Route>

            <Route
              path={ROUTES.PUBLIC_LOCALIZED}
              component={LocalizedPublicRoot}
            />

            <Route path={ROUTES.APP_HOME} component={AppRoot} />
            <Route
              path={INTERNAL_ROUTES.CHANNEL_DETAIL}
              component={ChannelDetailView}
            />
            <Route
              exact
              path={INTERNAL_ROUTES.CHANNEL_LIST}
              component={ChannelListView}
            />
            <Route exact path={INTERNAL_ROUTES.BASE} component={InternalRoot} />

            <Toaster richColors position="top-center" />
          </TooltipProvider>
        </I18nProvider>
      </AudioPlayerContextProvider>
      <TrackPageView />
    </BrowserRouter>
  </MessageProvider>
);
const rootApp = document.getElementById("root-app");

if (rootApp) {
  const root = createRoot(rootApp);
  root.render(
    <StrictMode>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <RootApp />
        </QueryClientProvider>
      </ErrorBoundary>
    </StrictMode>
  );
}
